import { useState } from 'react';
import { trpc } from '../../lib/trpc';

interface AuditLogViewerProps {
  className?: string;
}

type ActionFilter = 'media_cleanup' | 'media_permanent_delete' | 'entity_delete' | 'bulk_operation' | undefined;
type TargetTypeFilter = 'media' | 'venue' | 'wine' | 'event' | 'user' | undefined;



interface AuditLogDetails {
  deletedFromDatabase?: number;
  deletedFromCloudinary?: number;
  totalSize?: number;
  cloudinaryErrors?: string[];
  metadata?: {
    filename?: string;
    entityType?: string;
  };
  entityName?: string;
  entityType?: string;
  mediaMarkedDeleted?: number;
  referencesRemoved?: number;
  operation?: string;
  affectedCount?: number;
  reason?: string;
}

interface AuditLog {
  id: string;
  action: string;
  targetType: string;
  timestamp: string;
  performedBy: {
    name?: string;
    email: string;
  };
  details: AuditLogDetails;
}

export function AuditLogViewer({ className = '' }: AuditLogViewerProps) {
  const [actionFilter, setActionFilter] = useState<ActionFilter>(undefined);
  const [targetTypeFilter, setTargetTypeFilter] = useState<TargetTypeFilter>(undefined);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Get audit logs with filters
  const { data: auditLogs, isPending, refetch } = trpc.media.getAuditLogs.useQuery({
    action: actionFilter,
    targetType: targetTypeFilter,
    startDate: startDate ? new Date(startDate) : undefined,
    endDate: endDate ? new Date(endDate) : undefined,
    limit,
    offset,
  });

  const handleFilterChange = () => {
    setOffset(0);
    refetch();
  };

  const handleClearFilters = () => {
    setActionFilter(undefined);
    setTargetTypeFilter(undefined);
    setStartDate('');
    setEndDate('');
    setOffset(0);
  };

  const formatActionLabel = (action: string) => {
    switch (action) {
      case 'media_cleanup':
        return 'Media Cleanup';
      case 'media_permanent_delete':
        return 'Permanent Delete';
      case 'entity_delete':
        return 'Entity Delete';
      case 'bulk_operation':
        return 'Bulk Operation';
      default:
        return action;
    }
  };

  const formatDetails = (log: AuditLog) => {
    const details = log.details;
    
    switch (log.action) {
      case 'media_cleanup':
        return (
          <div className="text-sm text-gray-600">
            <div>Deleted {details.deletedFromDatabase} from database, {details.deletedFromCloudinary} from Cloudinary</div>
            <div>Total size: {formatBytes(details.totalSize ?? 0)}</div>
            {details.cloudinaryErrors && details.cloudinaryErrors.length > 0 && (
              <div className="text-red-600">Cloudinary errors: {details.cloudinaryErrors.length}</div>
            )}
          </div>
        );
      
      case 'media_permanent_delete':
        return (
          <div className="text-sm text-gray-600">
            <div>File: {details.metadata?.filename}</div>
            <div>Size: {formatBytes(details.totalSize ?? 0)}</div>
            <div>Entity: {details.metadata?.entityType}</div>
          </div>
        );
      
      case 'entity_delete':
        return (
          <div className="text-sm text-gray-600">
            <div>Entity: {details.entityName} ({details.entityType})</div>
            <div>Media marked deleted: {details.mediaMarkedDeleted}</div>
            <div>References removed: {details.referencesRemoved}</div>
          </div>
        );
      
      case 'bulk_operation':
        return (
          <div className="text-sm text-gray-600">
            <div>Operation: {details.operation}</div>
            <div>Affected: {details.affectedCount} items</div>
          </div>
        );
      
      default:
        return (
          <div className="text-sm text-gray-600">
            {details.reason ?? 'No additional details'}
          </div>
        );
    }
  };

  // Cast logs to proper type
  const logsList = (auditLogs?.logs ?? []) as unknown as AuditLog[];

  if (isPending) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Filters */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Audit Log Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Action
            </label>
            <select
              value={actionFilter ?? ''}
              onChange={(e) => setActionFilter(e.target.value === '' ? undefined : e.target.value as ActionFilter)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500"
            >
              <option value="">All Actions</option>
              <option value="media_cleanup">Media Cleanup</option>
              <option value="media_permanent_delete">Permanent Delete</option>
              <option value="entity_delete">Entity Delete</option>
              <option value="bulk_operation">Bulk Operation</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Type
            </label>
            <select
              value={targetTypeFilter ?? ''}
              onChange={(e) => setTargetTypeFilter(e.target.value === '' ? undefined : e.target.value as TargetTypeFilter)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500"
            >
              <option value="">All Types</option>
              <option value="media">Media</option>
              <option value="venue">Venue</option>
              <option value="wine">Wine</option>
              <option value="event">Event</option>
              <option value="user">User</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500"
            />
          </div>
        </div>
        
        <div className="flex space-x-4">
          <button
            onClick={handleFilterChange}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Audit Logs */}
      <div className="bg-gray-50 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Audit Logs</h3>
          <p className="text-sm text-gray-600 mt-1">
            {auditLogs?.total ?? 0} total entries
          </p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {logsList.map((log) => (
            <div key={log.id} className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      log.action === 'media_cleanup' ? 'bg-red-100 text-red-800' :
                      log.action === 'media_permanent_delete' ? 'bg-red-100 text-red-800' :
                      log.action === 'entity_delete' ? 'bg-orange-100 text-orange-800' :
                      log.action === 'bulk_operation' ? 'bg-primary-100 text-primary-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {formatActionLabel(log.action)}
                    </span>
                    
                    <span className="text-sm text-gray-500 capitalize">
                      {log.targetType}
                    </span>
                    
                    <span className="text-sm font-medium text-gray-900">
                      {log.performedBy.name ?? log.performedBy.email}
                    </span>
                  </div>
                  
                  {formatDetails(log)}
                  
                  {log.details.reason && (
                    <div className="text-xs text-gray-500 mt-1">
                      {log.details.reason}
                    </div>
                  )}
                </div>
                
                <div className="text-sm text-gray-500 ml-4">
                  {new Date(log.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
          
          {logsList.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">
              No audit logs found matching the current filters.
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {auditLogs && auditLogs.total > limit && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {offset + 1} to {Math.min(offset + limit, auditLogs.total)} of {auditLogs.total} entries
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= auditLogs.total}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to format bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}